import { createSelector } from 'reselect'
import { canAddInsuranceToOrder, haveOrdersBeenFetched, isItemCancelled, sortOrdersByDeparture, getIsCharterBundleOrder, hasVisibleCustomOfferItems } from 'lib/order/orderUtils'
import isUpcoming, { isExperienceItemUpcoming, isOrderCurrent } from 'lib/order/isUpcoming'
import { isEmpty, last, nonNullable, groupBy, isNonNullable, unique, uniqueBy } from 'lib/array/arrayUtils'
import { isOrderUpsellable } from 'actions/UpsellAndAvailableCreditSnackbar/utils'
import { isBedbankType } from 'lib/offer/offerTypes'
import { capitaliseFirstLetterOnly } from 'lib/string/stringUtils'
import { isLuxPlusSubscriptionInCart } from 'checkout/selectors/view/luxPlusSubscriptionItems'
import { getAllOffers } from './offerSelectors'
import { TRANSFER_ADDON, TRANSFER_COMPLIMENTARY_EXPERIENCE, TRANSFER_INCLUSION } from 'constants/transfers'
import { canAddExtraGuests, getExtraGuestCapacity } from 'lib/order/extraGuestUtils'
import { getPackageUniqueKey } from 'lib/offer/offerUtils'
import { matchLocaleAwarePath } from 'lib/routing/matchLocaleAwarePath'
import { canChangeHotelRooms, canExtendHotelDates, canRequestChangeHotelDates } from 'supportAssistant/utils/orderUtils'
import { canChangeBedbankGuestName } from 'components/Common/OrderView/OrderItemSummaries/BedbankItemSummary/BedbankRoomSummaryUtils'
import { isGroupPaymentOrder } from 'lib/payment/groupPaymentDetailsUtils'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import productLabels from 'i18n/productLabels'
import { formatInlineList } from 'lib/format/formatList'
import orderCancellationConfigMap from 'api/mappers/orderCancellationConfigMap'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import { getOptimizelyFeatureFlagVariation } from 'lib/optimizely/optimizelyUtils'
import { formatAirportTransferJourneyTitle, isLEDAirportTransfer } from 'components/Pages/MyEscapesPage/AirportTransfers/utils/airportTransferUtils'

const getOrderLocale = (state: App.State) => state.geo.locale
const getIsSpoofed = (state: App.State) => state.auth.account.isSpoofed

const toMappedConfig = (c?: App.CancellationConfig): App.OrderItemHotelCancellationConfig | undefined =>
  c ? orderCancellationConfigMap(c) : undefined

const AIRPORT_TRANSFER_TYPES = ['AIRPORT-TO-HOTEL', 'HOTEL-TO-AIRPORT']

const textHasAirportTransfer = (text: string) => {
  const lowerCaseText = text.toLowerCase()
  return lowerCaseText.includes('airport') && lowerCaseText.includes('transfer')
}

const getOrderBookingDetail = (order: App.Order): App.OrderBookingDetail => {
  let orderItem: App.OrderItem | App.OrderBedbankItem = order.items[0]
  if (order.bedbankItems.length > 0) {
    orderItem = order.bedbankItems[0]
  }

  if (order.items.length > 0) {
    const item = orderItem as App.OrderItem
    return {
      id: order.id,
      startDate: item.reservation?.startDate,
      endDate: item.reservation?.endDate,
      bookingNumber: item.bookingNumber,
      latitude: item?.offer.property?.latitude,
      longitude: item?.offer.property?.longitude,
      locationSubheading: item?.offer.property?.locationSubheading,
      status: item.status,
    }
  }

  const bedbankItem = orderItem as App.OrderBedbankItem
  if (bedbankItem) {
    return {
      id: order.id,
      startDate: bedbankItem.checkIn.toDate().toISOString(),
      endDate: bedbankItem.checkOut.toDate().toISOString(),
      bookingNumber: bedbankItem.bookingNumber,
      latitude: bedbankItem?.offer.property?.latitude,
      longitude: bedbankItem?.offer.property?.longitude,
      locationSubheading: bedbankItem?.offer.property?.location,
      status: bedbankItem.status,
    }
  }

  return {
    id: order.id,
    bookingNumber: order.bookingNumber,
    status: order.status,
  }
}

export const getOrdersThatCanHaveInsuranceAdded = createSelector(
  (state: App.State) => state.orders.orders,
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => state.offer.offers,
  (orders, regionCode, offers) => {
    return Object.values(orders).filter(order => {
      const offer = offers[order.items[0]?.offerId]
      return canAddInsuranceToOrder(order, regionCode, offer)
    })
  },
)

export const getOrderOffers = createSelector(
  (state: App.State) => state.offer.offers,
  (state: App.State, offerIds: Array<string>) => offerIds,
  (offers: Record<string, App.Offer>, offerIds: Array<string>) => {
    return nonNullable(offerIds.map(offerId => offers[offerId]))
  },
)

export const getUpcomingOrders = createSelector(
  (state: App.State) => state.orders.orders,
  getIsSpoofed,
  (orders, isSpoofed) => Object.values(orders).filter(order => isUpcoming(order, isSpoofed)),
)

/** Future check-ins plus in-trip/active stays. */
export const getUpcomingAndCurrentOrders = createSelector(
  (state: App.State) => state.orders.orders,
  getIsSpoofed,
  (orders, isSpoofed) =>
    Object.values(orders).filter(
      o => o.status !== 'cancelled' && (isUpcoming(o, isSpoofed) || isOrderCurrent(o, isSpoofed)),
    ),
)

export const getExperienceAirportTransferOrders = createSelector(
  (state: App.State) => state.orders.orders,
  (orders) => {
    return Object.values(orders).filter(order => order.transferItems.some(item => AIRPORT_TRANSFER_TYPES.includes(item.type) && item.status !== 'cancelled'))
  },
)

export const getAddonAirportTransferOrders = createSelector(
  (state: App.State) => state.orders.orders,
  (orders) => {
    const ordersWithAddons = Object.values(orders).filter(order => order.addonItems.length > 0)
    return ordersWithAddons.filter(
      order => order.addonItems.some(
        addonItem => addonItem.categories?.some(category => category === 'Transport') && addonItem.status !== 'cancelled',
      ),
    )
  },
)

export const getBedbankInclusionAirportTransferOrderIds = createSelector(
  (state: App.State) => state.orders.orders,
  (orders) => {
    const bedbankOrderIdsWithAirportTransferInclusions = unique(
      Object.values(orders)
        .filter(order => order.bedbankItems.length > 0)
        .filter(order => order.bedbankItems.some(item => {
          const rateInclusions = item.offer.packages.find(pkg => pkg.roomId === item.roomTypeId)?.rate?.inclusions.map(i => i.description) || []
          const promotionInclusions = item.offer.promotions.flatMap(promo => promo.rateInclusionsLong)
          const facilitiesInclusions = item.rooms.flatMap(room => room.facilities.map(facility => facility.description))

          return [...rateInclusions, ...promotionInclusions, ...facilitiesInclusions].some(text => textHasAirportTransfer(text))
        }))
        .map(order => order.id),
    )

    return bedbankOrderIdsWithAirportTransferInclusions
  },
)

export const getHotelInclusionAirportTransferOrderIds = createSelector(
  (state: App.State) => state.orders.orders,
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (orders, isLuxPlusInCart) => {
    const orderInclusionsMap: Array<{ orderId: string, inclusions: Array<App.OrderItemInclusions> }> = Object.values(orders).flatMap((order: App.Order) => ({
      orderId: order.id,
      inclusions: order.items.flatMap((item: App.OrderItem) => item.inclusions),
    })).filter(order => order.inclusions.length > 0)

    const orderIdsWithAirportTransferInclusion = unique(
      orderInclusionsMap.filter((order) => {
        const allInclusionsText = order.inclusions.flatMap(inclusion => {
          const inclusionHighlights = inclusion.inclusionHighlights || []
          const inclusions = inclusion.inclusions || []
          const luxPlusInclusions = isLuxPlusInCart ? inclusion.luxPlusInclusions || [] : []
          return [
            ...inclusionHighlights.map(highlight => highlight.description),
            ...inclusions.map(inclusion => inclusion.description),
            ...luxPlusInclusions.map(inclusion => inclusion.description),
          ]
        }).filter(isNonNullable)

        const hasTransferInInclusion = allInclusionsText.some(inclusion => textHasAirportTransfer(inclusion))
        return hasTransferInInclusion
      }).map(order => order.orderId))

    return orderIdsWithAirportTransferInclusion
  },
)

export const getInclusionAirportTransferOrders = createSelector(
  (state: App.State) => state.orders.orders,
  getHotelInclusionAirportTransferOrderIds,
  getBedbankInclusionAirportTransferOrderIds,
  (orders, hotelOrderIdsWithAirportTransferInclusions, bedbankOrderIdsWithAirportTransferInclusions) => {
    const allOrderIdsWithAirportTransferInclusions = unique([...hotelOrderIdsWithAirportTransferInclusions, ...bedbankOrderIdsWithAirportTransferInclusions])
    return Object.values(orders).filter((order: App.Order) => allOrderIdsWithAirportTransferInclusions.includes(order.id))
  },
)

export const getOrdersWithAirportTransfers = createSelector(
  (state: App.State) => state.orders.orders,
  (state: App.State) => state.experience.experiences,
  getAllOffers,
  getExperienceAirportTransferOrders,
  getAddonAirportTransferOrders,
  getInclusionAirportTransferOrders,
  (orders, experienceOffers, offers, ordersWithExperienceAirportTransfers, ordersWithAddonAirportTransfers, ordersWithInclusionAirportTransfers): Array<App.OrderWithAirportTransfers> => {
    const uniqueOrderIds = unique([
      ...ordersWithExperienceAirportTransfers.map(order => order.id),
      ...ordersWithAddonAirportTransfers.map(order => order.id),
      ...ordersWithInclusionAirportTransfers.map(order => order.id),
    ])

    const orderIdsExperienceAirportTransfers = ordersWithExperienceAirportTransfers.map(order => order.id)
    const orderIdsAddonAirportTransfers = ordersWithAddonAirportTransfers.map(order => order.id)
    const orderIdsInclusionAirportTransfers = ordersWithInclusionAirportTransfers.map(order => order.id)

    const ordersWithAirportTransfers = Object.values(orders).filter(order => uniqueOrderIds.includes(order.id)).map((order: App.Order) => {
      const airportTransferTypes: Array<string> = []
      const airportTransferProviders: Array<App.AirportTransferProvider> = []
      let airportTransferTitlesAndPickupTimes: Array<App.AirportTransferTitleAndPickupTime> = []

      const isExperienceAirportTransfer = orderIdsExperienceAirportTransfers.includes(order.id)
      const isInclusionAirportTransfer = orderIdsInclusionAirportTransfers.includes(order.id)
      const isAddonAirportTransfer = orderIdsAddonAirportTransfers.includes(order.id)

      if (isAddonAirportTransfer) {
        airportTransferTypes.push(TRANSFER_ADDON)
        const addonItems = order.addonItems.filter(
          addonItem =>
            addonItem.categories?.some(category => category === 'Transport'),
        )

        const addonItem = addonItems[0]

        if (addonItem) {
          airportTransferProviders.push({
            vendorBookingEmail: addonItem.vendorBookingEmail,
            vendorBookingPhone: addonItem.vendorContactPhone,
            vendorName: addonItem.channelProvider === 'ics' ? addonItem.channelProvider?.toUpperCase() : capitaliseFirstLetterOnly(addonItem.channelProvider || ''),
            transferType: TRANSFER_ADDON,
          })

          airportTransferTitlesAndPickupTimes = uniqueBy(addonItems.map(item => ({
            title: item.name,
          })), (item) => item.title)
        }
      }

      if (isExperienceAirportTransfer) {
        airportTransferTypes.push(TRANSFER_COMPLIMENTARY_EXPERIENCE)

        let experienceOffer = experienceOffers[order.experienceItems[0]?.experienceId]
        let transferItem: App.OrderTransferItem | null = null
        if (order.transferItems.length > 0) {
          transferItem = order.transferItems[0]
          experienceOffer = experienceOffers[transferItem.option.experienceId]
        }

        if (experienceOffer) {
          airportTransferProviders.push({
            vendorBookingEmail: experienceOffer?.vendor?.bookingEmail,
            vendorBookingPhone: experienceOffer?.vendor?.bookingPhone,
            vendorName: experienceOffer?.vendor?.name,
            transferType: TRANSFER_COMPLIMENTARY_EXPERIENCE,
          })

          airportTransferTitlesAndPickupTimes = uniqueBy(order.transferItems.map(item => ({
            title: item.option.name,
            pickupTime: item.pickupTime ?? undefined,
            pickupDate: item.pickupDate ?? undefined,
          })), (item) => `${item.title}-${item.pickupTime}-${item.pickupDate}`)
        }
      }

      if (isInclusionAirportTransfer) {
        airportTransferTypes.push(TRANSFER_INCLUSION)

        const isBedbank = order.bedbankItems.length > 0
        if (isBedbank) {
          const bedbankOffer = offers[order.bedbankItems[0]?.offer?.id] as App.BedbankOffer
          if (bedbankOffer) {
            airportTransferProviders.push({
              vendorBookingPhone: bedbankOffer.property.phone,
              vendorName: bedbankOffer.property.name,
              transferType: TRANSFER_INCLUSION,
            })
          }
        } else {
          const offer = offers[order.items[0]?.offerId] as App.HotelOffer
          if (offer) {
            airportTransferProviders.push({
              vendorBookingEmail: offer.vendorBookingEmail,
              vendorBookingPhone: offer.vendorContactPhone,
              vendorName: offer.property?.name,
              transferType: TRANSFER_INCLUSION,
            })
          }
        }
      }

      const orderBookingDetail = getOrderBookingDetail(order)
      return {
        ...orderBookingDetail,
        airportTransferTypes,
        airportTransferProviders,
        airportTransferTitlesAndPickupTimes,
      }
    })

    return ordersWithAirportTransfers
  },
)

export const getOrderBookingNumbers = createSelector(
  (state: App.State) => state.orders.orders,
  (state: App.State) => state.offer.offers,
  (state: App.State) => state.orders.cancellationConfigs,
  (state: App.State) => getOptimizelyFeatureFlagVariation(state, OptimizelyFeatureFlags.opexSelfServiceNameChange),
  (orders, offers, cancellationConfigs, isSelfServiceNameChangeEnabled): Array<App.OrderBookingNumber> => {
    return Object.values(orders).flatMap((order) => {
      const { id, bookingNumber, items, bedbankItems, tourItems, experienceItems, carHireItems, flightItems, accessDetails } = order
      const canEditOrder = Boolean(accessDetails?.canEdit)
      const orderCancellationConfig = cancellationConfigs?.[order?.id] || {}
      // Keep this source location text in English: svc-support currently uses it for region-specific support logic.
      let locationSubheading = items[0]?.offer?.property?.locationSubheading
      if (isBedbankType(bedbankItems[0]?.offer?.type)) {
        locationSubheading = bedbankItems[0]?.offer?.property?.location
      }

      const offer = offers[items[0]?.offerId]
      const isCharterOrder = getIsCharterBundleOrder(order)
      const isGroupPayment = isGroupPaymentOrder(order)

      // Shared capability flags computed once per order
      const hasActiveCarHire = carHireItems.some(item => item.status !== 'cancelled')
      const canUpsellInsurance = !order.insuranceItems?.length && offer ? canAddInsuranceToOrder(order, order.regionCode, offer) : false
      const canDownloadInvoice = !isCharterOrder
      const canShareBooking = order.isBookingOwner ?? false

      const itemObjects = items.map(item => {
        const itemCancellationConfig = orderCancellationConfig?.[item.id]
        const mappedConfig = toMappedConfig(itemCancellationConfig)
        const itemOffer = offers[item.offerId] as App.HotelOffer | undefined
        const property = item?.offer?.property
        const hasMapCoordinates = !!(property?.latitude && property?.longitude)

        const isHotelItem = item?.offer?.type === 'hotel'
        const isCancelled = item.status === 'cancelled'
        const isBeforeCheckIn = item.reservation?.startDate ? new Date() < new Date(item.reservation.startDate) : false
        const canAddSpecialRequest = isHotelItem && isBeforeCheckIn && !isCancelled && canEditOrder
        const orderType = item?.offer?.type

        return {
          id,
          startDate: item.reservation?.startDate,
          endDate: item.reservation?.endDate,
          bookingNumber: item.bookingNumber,
          latitude: property?.latitude,
          longitude: property?.longitude,
          locationSubheading,
          status: item.status,
          orderType,
          canEditOrder,
          canAddExtraGuests: !isGroupPayment && !isCharterOrder && !!itemOffer && canAddExtraGuests(item, [], { ignoreAvailableRates: true, canAddExtraGuests: itemCancellationConfig?.can_add_extra_guests ?? false }),
          extraGuestDetails: {
            maxCapacity: itemOffer ? getExtraGuestCapacity(itemOffer, item, getPackageUniqueKey(item.package.id, item.package.duration, (item.package as App.OrderItemHotelPackage).roomRateId)) : null,
          },
          canChangeDates: !isGroupPayment && (itemCancellationConfig?.can_change_dates ?? false),
          canRequestChangeDates: !isGroupPayment && !!itemOffer && canRequestChangeHotelDates(order, itemOffer, mappedConfig),
          canBeCancelled: !!itemCancellationConfig?.can_refund,
          canRequestRefund: itemCancellationConfig?.can_request_refund,
          canRefundToOrigin: itemCancellationConfig?.can_refund_to_origin || false,
          canRefundToCredit: itemCancellationConfig?.can_refund_to_credit || false,
          changeOfMindEndDate: itemCancellationConfig?.change_of_mind_end_date ?? undefined,
          canDownloadInvoice,
          canShareBooking,
          hasMapCoordinates,
          canAddSpecialRequest,
          canChangeGuestName: isSelfServiceNameChangeEnabled && (mappedConfig?.canChangeGuestName ?? false),
          canChangeRooms: !isGroupPayment && !!itemOffer && canChangeHotelRooms(order, itemOffer, items, item, mappedConfig),
          canExtendDates: !isGroupPayment && !!itemOffer && canExtendHotelDates(order, itemOffer, item.id, mappedConfig),
          hasCarHire: hasActiveCarHire,
          canUpsellInsurance,
          daysBeforeCheckInChangesDisallowed: itemOffer?.daysBeforeCheckInChangesDisallowed,
          freeCancellationPeriodEndDate: itemCancellationConfig?.policy_deadline,
        }
      })

      const bedbankItemObjects = bedbankItems.map(item => {
        // Bedbank cancellation configs by room.roomId (not item.id) - matching My Escapes
        const primaryRoom = item.rooms?.find(room => room.status !== 'cancelled') ?? item.rooms?.[0]
        const itemCancellationConfig = primaryRoom ? orderCancellationConfig?.[primaryRoom.roomId] : undefined
        const eligibleNameChangeRoom = item.rooms?.find(room => {
          const roomConfig = toMappedConfig(orderCancellationConfig?.[room.roomId])
          return canChangeBedbankGuestName({
            isSelfServiceNameChangeEnabled,
            canEdit: canEditOrder,
            item,
            room,
            canChangeGuestName: roomConfig?.canChangeGuestName,
          })
        })
        const property = item?.offer?.property
        const hasMapCoordinates = !!(property?.latitude && property?.longitude)

        return {
          id,
          startDate: item.checkIn,
          endDate: item.checkOut,
          bookingNumber: item.bookingNumber,
          latitude: property?.latitude,
          longitude: property?.longitude,
          locationSubheading: property?.location,
          status: item.status,
          orderType: 'bedbank',
          canEditOrder,
          canChangeDates: !isGroupPayment && itemCancellationConfig?.can_change_dates,
          canBeCancelled: itemCancellationConfig?.can_refund,
          canRequestRefund: itemCancellationConfig?.can_request_refund,
          canRefundToOrigin: itemCancellationConfig?.can_refund_to_origin || false,
          canRefundToCredit: itemCancellationConfig?.can_refund_to_credit || false,
          changeOfMindEndDate: itemCancellationConfig?.change_of_mind_end_date ?? undefined,
          freeCancellationPeriodEndDate: itemCancellationConfig?.policy_deadline,
          canDownloadInvoice,
          canShareBooking,
          hasMapCoordinates,
          canChangeGuestName: Boolean(eligibleNameChangeRoom),
          roomId: eligibleNameChangeRoom?.roomId ?? primaryRoom?.roomId,
          hasCarHire: hasActiveCarHire,
          canUpsellInsurance,
        }
      })
      const tourItemObjects = tourItems.map(item => ({
        id,
        bookingNumber: item.bookingId,
        status: item.status,
        orderType: 'tour',
        canEditOrder,
        canDownloadInvoice,
        canShareBooking,
      }))
      const experienceItemObjects = experienceItems.map(item => ({
        id,
        bookingNumber: item.bookingNumber,
        status: item.status,
        orderType: 'experience',
        canEditOrder,
        canDownloadInvoice,
        canShareBooking,
      }))
      const carHireItemObjects = carHireItems.map(item => ({
        id,
        bookingNumber,
        status: item.status,
        orderType: 'car_hire',
        canEditOrder,
        canDownloadInvoice,
        canShareBooking,
      }))

      const flightItemObjects = flightItems.map(item => ({
        id,
        bookingNumber: item.pnrId,
        status: item.status,
        departureDate: item.departureDate,
        orderType: 'flight',
        canEditOrder,
        canDownloadInvoice,
        canShareBooking,
      }))

      return [...itemObjects, ...bedbankItemObjects, ...tourItemObjects, ...experienceItemObjects, ...carHireItemObjects, ...flightItemObjects]
    })
  },
)

export const getPendingOrders = createSelector(
  (state: App.State) => state.orders.orders,
  (orders) => Object.values(orders).filter(order => order.status === 'pending'),
)

const getNonEmptyOrders = (orders: Array<App.Order>, isSpoofed = false) => orders.filter(order =>
  !isEmpty(order.items) ||
  !isEmpty(order.bedbankItems) ||
  !isEmpty(order.flightItems) ||
  !isEmpty(order.tourItems) ||
  !isEmpty(order.experienceItems) ||
  !isEmpty(order.cruiseItems) ||
  !isEmpty(order.carHireItems) ||
  !isEmpty(order.transferItems) ||
  hasVisibleCustomOfferItems(order, isSpoofed) ||
  !isEmpty(order.merchandiseItems),
)

export const getNextUpsellableOrder = createSelector(
  (state: App.State) => getUpcomingOrders(state),
  getIsSpoofed,
  (orders, isSpoofed): App.Order => {
    const sortedOrders = sortOrdersByDeparture(
      getNonEmptyOrders(orders, isSpoofed).filter(
        (order) => order.status !== 'cancelled' && isOrderUpsellable(order),
      ),
    )
    return sortedOrders[0]
  },
)

export const getSortedOrdersByDeparture = createSelector(
  (state: App.State) => haveOrdersBeenFetched(state),
  (state: App.State) => state.orders.orders,
  getIsSpoofed,
  (ordersFetched, orders, isSpoofed) => {
    const allUnsortedOrders = getNonEmptyOrders(Object.values(orders), isSpoofed)
    return {
      fetched: ordersFetched,
      data: sortOrdersByDeparture(allUnsortedOrders),
    }
  },
)

export const getUpcomingExperienceItems = createSelector(
  (state: App.State) => state.orders.orders,
  (orders) => {
    return Object.values(orders).filter(Boolean).flatMap(order => {
      return order.experienceItems.filter(item => isExperienceItemUpcoming(item, order))
    })
  },
)

type OrderTypeId =
  'tours' |
  'accommodation' |
  'carHire' |
  'cruises' |
  'flights' |
  'flight' |
  'experiences' |
  'transfers' |
  'insurance' |
  'luxPlus' |
  'merchandise' |
  'cruise' |
  'tour' |
  'hotel' |
  'experience' |
  'transfer' |
  'other'

interface OrderTypeInfo {
  typeId: OrderTypeId | string
  type: string
  count: number
  primaryName?: string
}

function getOrderTypeLabel(typeId: OrderTypeId | string): string {
  const intl = getCurrentIntl()

  switch (typeId) {
    case 'tours':
      return intl.formatMessage(productLabels.tours)
    case 'accommodation':
      return intl.formatMessage(productLabels.accommodation)
    case 'carHire':
      return intl.formatMessage(productLabels.carHire)
    case 'car_hire':
      return getOrderTypeLabel('carHire')
    case 'cruises':
      return intl.formatMessage(productLabels.cruises)
    case 'flights':
      return intl.formatMessage(productLabels.flights)
    case 'flight':
      return intl.formatMessage(productLabels.flight)
    case 'experiences':
      return intl.formatMessage(productLabels.experiences)
    case 'transfers':
      return intl.formatMessage({
        id: 'jcYuhDu0a-0cnYQHcLHa',
        defaultMessage: 'Transfers',
        description: 'Plural order type label for transfer bookings in the order summary heading',
      })
    case 'insurance':
      return intl.formatMessage({
        id: 'baoIxa7gUvqD8uchJwSf',
        defaultMessage: 'Insurance',
        description: 'Order type label for insurance items in the order summary heading',
      })
    case 'luxPlus':
      return intl.formatMessage({
        id: 'srCMMsouxtlXlQS1QfFi',
        defaultMessage: 'LuxPlus+ Membership',
        description: 'Human-readable label for the LuxPlus+ membership item type',
      })
    case 'luxplus':
      return getOrderTypeLabel('luxPlus')
    case 'merchandise':
      return intl.formatMessage({
        id: 'tEjbFJ_lY_ONGYfkcIyV',
        defaultMessage: 'Merchandise',
        description: 'Order type label for merchandise items in the order summary heading',
      })
    case 'cruise':
      return intl.formatMessage(productLabels.cruise)
    case 'tour':
      return intl.formatMessage(productLabels.tour)
    case 'hotel':
      return intl.formatMessage(productLabels.hotel)
    case 'experience':
      return intl.formatMessage(productLabels.experience)
    case 'transfer':
      return intl.formatMessage({
        id: 'DAm4zasFQe4Hi-QPsGJX',
        defaultMessage: 'Transfer',
        description: 'Singular order type label for a transfer custom offer item in the order summary heading',
      })
    case 'other':
      return intl.formatMessage({
        id: 'jMDGScexB8mKv6mkITtY',
        defaultMessage: 'Other',
        description: 'Fallback order type label for uncategorised order items in the order summary heading',
      })
    default:
      return capitaliseFirstLetterOnly(typeId)
  }
}

export const getOrderOfferTypes = createSelector(
  (state: App.State, order: App.Order) => order,
  (state: App.State) => getAllOffers(state),
  (state: App.State) => state.carHire.reservationInfo,
  (state: App.State) => state.experience.experiences,
  (state: App.State) => state.orders.flightDetails,
  (state: App.State) => state.cruise.cruiseOfferSnapshot,
  getOrderLocale,
  (order, offers, carHireReservationInfo, experiences, flightDetails, cruiseOfferSnapshots, _locale): Array<OrderTypeInfo> => {
    const intl = getCurrentIntl()
    const types: Array<OrderTypeInfo> = []
    if (!offers) return types

    if (order.tourItems.length > 0) {
      const activeTourItems = order.tourItems.filter((item: App.Tours.TourV2OrderItem) => !isItemCancelled(item))
      const groupedTours = groupBy(
        activeTourItems,
        (item) => `${item.tourId}_${item.tour?.startDate}_${item.tour?.endDate}`,
      )

      if (groupedTours.size > 0) {
        const tourOffer = offers[order.tourItems[0]?.tourId]
        types.push({
          typeId: 'tours',
          type: getOrderTypeLabel('tours'),
          count: groupedTours.size,
          primaryName: tourOffer?.name,
        })
      }
    }

    const hasAccommodation = order.items.length > 0 || order.bedbankItems.length > 0
    if (hasAccommodation) {
      const activeItems = order.items.filter((item: App.OrderItem) => !isItemCancelled(item))
      const activeBedbankItems = order.bedbankItems.filter((item: App.OrderBedbankItem) => !isItemCancelled(item))

      const groupedAccommodations = groupBy(
        [...activeItems, ...activeBedbankItems],
        (item) => {
          if ('offerId' in item) {
            return `${item.offerId}_${item.reservation?.startDate}_${item.reservation?.endDate}`
          }
          return `${item.offer.id}_${item.checkIn?.toISOString()}_${item.checkOut?.toISOString()}`
        },
      )

      if (groupedAccommodations.size > 0) {
        const firstAccommodation = order.items[0]?.offer || order.bedbankItems[0]?.offer
        types.push({
          typeId: 'accommodation',
          type: getOrderTypeLabel('accommodation'),
          count: groupedAccommodations.size,
          primaryName: firstAccommodation?.property?.name || firstAccommodation?.name,
        })
      }
    }

    if (order.carHireItems.length > 0) {
      const activeCarHireItems = order.carHireItems.filter((item: App.OrderCarHireItem) => !isItemCancelled(item))
      const groupedCarHire = groupBy(
        activeCarHireItems,
        (item) => `${item.idReservation}_${item.reservation?.pickUp.date}_${item.reservation?.dropOff.date}`,
      )

      if (groupedCarHire.size > 0) {
        const reservationId = order.carHireItems[0]?.idReservation || ''
        const reservationInfo = carHireReservationInfo[reservationId]?.reservation
        types.push({
          typeId: 'carHire',
          type: getOrderTypeLabel('carHire'),
          count: groupedCarHire.size,
          primaryName: reservationInfo?.vehicle?.model
            ? intl.formatMessage(
                {
                  id: 'evqNMKRw8xM1hNTzis_c',
                  defaultMessage: '{vehicleModel} Car Hire',
                  description: 'Primary order item name for a car hire booking with the vehicle model',
                },
                { vehicleModel: reservationInfo.vehicle.model },
              )
            : getOrderTypeLabel('carHire'),
        })
      }
    }

    if (order.cruiseItems.length > 0) {
      const activeCruiseItems = order.cruiseItems.filter((item: App.OrderCruiseItem) => !isItemCancelled(item))
      const groupedCruises = groupBy(
        activeCruiseItems,
        (item) => `${item.cruiseOfferId}_${item.arrivalDate}`,
      )

      if (groupedCruises.size > 0) {
        const firstCruiseItem = activeCruiseItems[0]
        const offerSnapshot = firstCruiseItem ? cruiseOfferSnapshots[firstCruiseItem.bookingId] : undefined

        types.push({
          typeId: 'cruises',
          type: getOrderTypeLabel('cruises'),
          count: groupedCruises.size,
          primaryName: offerSnapshot?.offerName ?? firstCruiseItem?.offerName,
        })
      }
    }

    if (order.flightItems.length > 0) {
      const activeFlightItems = order.flightItems.filter((item: App.OrderFlightItem) => !isItemCancelled(item))
      const groupedFlights = groupBy(
        activeFlightItems,
        (item) => `${item.pnrId}_${item.departureDate}`,
      )

      if (groupedFlights.size > 0) {
        const firstFlightDetails = flightDetails[order.flightItems[0]?.id]?.flight
        const lastFlightDetails = order.flightItems.length > 1 ?
          flightDetails[order.flightItems[order.flightItems.length - 1]?.id]?.flight :
          undefined

        const legs: Array<App.Leg> = (() => {
          if (firstFlightDetails && lastFlightDetails) {
            return [...(firstFlightDetails.bookingJourney?.legs || []), ...(lastFlightDetails.bookingJourney?.legs || [])]
          } else if (firstFlightDetails) {
            return firstFlightDetails.bookingJourney?.legs || []
          }
          return []
        })()

        let tripType = ''
        if (legs.length === 2 && firstFlightDetails && !lastFlightDetails) {
          tripType = 'Return'
        } else if (firstFlightDetails && lastFlightDetails) {
          tripType = 'TwoOneWay'
        } else if (legs.length === 1) {
          tripType = 'OneWay'
        } else if (legs.length > 2) {
          tripType = 'MultiCity'
        } else if (legs.length === 2) {
          const isOpenJaw = legs[0].flights && legs[1].flights &&
            last(legs[0].flights).arrivalAirport !== legs[1].flights[0].departureAirport
          const isReverseOpenJaw = legs[0].flights && legs[1].flights &&
            last(legs[1].flights).arrivalAirport !== legs[0].flights[0].departureAirport
          if (isOpenJaw || isReverseOpenJaw) {
            tripType = 'MultiCity'
          }
        }

        let flightTitle = ''
        if (tripType === 'MultiCity') {
          const uniqueCities = new Set()
          legs.forEach(leg => {
            uniqueCities.add(leg.flights?.[0]?.departureCity)
            uniqueCities.add(last(leg.flights)?.arrivalCity)
          })
          const cities: Array<string> = Array.from(uniqueCities) as Array<string>
          flightTitle = intl.formatMessage(
            {
              id: 'b3JMDicwK0VzecCO0-s9',
              defaultMessage: 'Flights to {destinations}',
              description: 'Primary order item name for a multi-city flight booking with the destination list',
            },
            { destinations: formatInlineList(cities, 'conjunction') },
          )
        } else if (legs.length > 0) {
          const firstLeg = legs[0]
          const departingFlight = firstLeg.flights?.[0]
          const returningFlight = last(firstLeg.flights)

          if (departingFlight?.departureAirportName &&
            departingFlight?.departureCountry &&
            returningFlight?.arrivalAirportName &&
            returningFlight?.arrivalCountry) {
            flightTitle = intl.formatMessage(
              {
                id: '0HlEdKu9V02sZBDgcfsR',
                defaultMessage: '{departureAirport}, {departureCountry} to {arrivalAirport}, {arrivalCountry}',
                description: 'Primary order item name for a flight booking from one airport and country to another',
              },
              {
                departureAirport: departingFlight.departureAirportName,
                departureCountry: departingFlight.departureCountry,
                arrivalAirport: returningFlight.arrivalAirportName,
                arrivalCountry: returningFlight.arrivalCountry,
              },
            )
          }
        }

        types.push({
          typeId: 'flights',
          type: getOrderTypeLabel('flights'),
          count: groupedFlights.size,
          primaryName: flightTitle || getOrderTypeLabel('flights'),
        })
      }
    }

    if (order.experienceItems.length > 0) {
      const activeExperienceItems = order.experienceItems.filter((item: App.OrderExperienceItem) => !isItemCancelled(item))
      const groupedExperiences = groupBy(
        activeExperienceItems,
        (item) => `${item.experienceId}_${item.date}_${item.time}`,
      )

      if (groupedExperiences.size > 0) {
        types.push({
          typeId: 'experiences',
          type: getOrderTypeLabel('experiences'),
          count: groupedExperiences.size,
          primaryName: order.experienceItems[0].title,
        })
      }
    }

    if (order.transferItems.length > 0) {
      const airportTransferItems = order.transferItems.filter(transfer => !isLEDAirportTransfer(transfer))
      const activeAirportTransferItems = airportTransferItems.filter((item: App.OrderTransferItem) => !isItemCancelled(item))

      if (activeAirportTransferItems.length > 0) {
        const airportTransferTitle = formatAirportTransferJourneyTitle(intl, activeAirportTransferItems)
        types.push({
          typeId: 'airportTransfers',
          type: airportTransferTitle,
          count: 1,
          primaryName: airportTransferTitle,
        })
      }

      const activeTransferItems = order.transferItems.filter((item: App.OrderTransferItem) => {
        return isLEDAirportTransfer(item) && !isItemCancelled(item)
      })
      const groupedTransfers = groupBy(
        activeTransferItems,
        (item) => `${item.experienceId}_${item.date}_${item.type}`,
      )

      if (groupedTransfers.size > 0) {
        const firstExperienceTransfer = activeTransferItems[0]
        types.push({
          typeId: 'transfers',
          type: getOrderTypeLabel('transfers'),
          count: groupedTransfers.size,
          primaryName: experiences[firstExperienceTransfer?.experienceId]?.name,
        })
      }
    }

    if (order.customOfferItems?.[0]) {
      const activeCustomOfferItems = order.customOfferItems.filter((item: App.OrderCustomOfferItem) => !isItemCancelled(item))
      const groupedCustomOffers = groupBy(
        activeCustomOfferItems,
        (item) => `${item.custom_offer.id}_${item.custom_offer.items[0].type}`,
      )

      if (groupedCustomOffers.size > 0) {
        const customOffer = order.customOfferItems[0]
        const type = customOffer.custom_offer.items[0].type
        types.push({
          typeId: type,
          type: getOrderTypeLabel(type),
          count: groupedCustomOffers.size,
          primaryName: customOffer.custom_offer.items[0].name,
        })
      }
    }

    if (order.insuranceItems.length > 0 || order.bookingProtectionItems.length > 0) {
      types.push({
        typeId: 'insurance',
        type: getOrderTypeLabel('insurance'),
        count: 1,
        primaryName: getOrderTypeLabel('insurance'),
      })
    }

    if (order.subscriptionItems?.length > 0) {
      types.push({
        typeId: 'luxPlus',
        type: getOrderTypeLabel('luxPlus'),
        count: 1,
        primaryName: getOrderTypeLabel('luxPlus'),
      })
    }

    if (order.merchandiseItems.length > 0) {
      types.push({
        typeId: 'merchandise',
        type: getOrderTypeLabel('merchandise'),
        count: order.merchandiseItems.length,
        primaryName: order.merchandiseItems[0].snapshot.name,
      })
    }

    return types
  },
)

export const getTravelOrderOfferTypes = createSelector(
  (state: App.State, order: App.Order) => getOrderOfferTypes(state, order),
  (orderTypes) => {
    return orderTypes.filter(item => item.typeId !== 'merchandise')
  },
)

export const getViewedOrder = createSelector(
  (state: App.State) => state.router.location,
  (state: App.State) => state.orders.orders,
  (location, orders): App.Order | undefined => {
    let viewedOrderId = ''
    const myEscapesMatch = matchLocaleAwarePath<{ orderId: string }>(location.pathname, '/:regionCode/account/my-escapes/order/:orderId')
    const confirmationMatch = matchLocaleAwarePath(location.pathname, '/:regionCode/booking/confirmation')

    if (myEscapesMatch) {
      viewedOrderId = myEscapesMatch.params.orderId
    } else if (confirmationMatch) {
      viewedOrderId = new URLSearchParams(location.search).get('orderId') ?? ''
    }
    return orders[viewedOrderId]
  },
)
